/**
 * Adapters onto the shared conversation envelope rendered by
 * <harmony-conversation> (@harmonyso/conversation-viewer).
 *
 * The envelope is {thread, messages, runs}: `messages` is the visible dialogue,
 * `runs` is what the agent did. Only `runs` is required, so a source that has
 * one track and not the other still renders.
 *
 * This app reads conversations-viewer, which is a lossy projection of the
 * threads/agents tables the envelope mirrors — so both of its shapes downcast
 * in cleanly:
 *   - ConversationDetailResponse.turns -> messages   (the visible track)
 *   - RawConversation.turns[].agent_responses -> runs (the internal track)
 */
import type { ConversationDetail, RawConversation } from '@/lib/types/conversations';

import type { ConversationEnvelope } from '@harmonyso/conversation-viewer';

export type { ConversationEnvelope };

const LOGFIRE = 'https://logfire-eu.pydantic.dev/harmony/agents-api';

/** Thread-scoped Logfire query. The S3/DB data carries no system prompt,
 *  token counts or errors; Logfire has all three. */
export function logfireUrl(threadId: string, env = 'prod'): string {
  const q = `attributes->>'harmony.thread.id' = '${threadId}'`;
  return `${LOGFIRE}?${new URLSearchParams({ q, last: '"30d"', env }).toString()}`;
}

/** conversations-viewer detail -> the visible track. */
export function fromDetail(detail?: ConversationDetail | null): ConversationEnvelope {
  const turns = detail?.turns ?? [];

  // conversations-viewer carries employee_name per turn; without collecting it
  // the viewer falls back to raw ids like "kAsfwIGYe9Rp".
  // Built via fromEntries rather than keyed assignment: eslint's
  // security/detect-object-injection flags the latter, and CI runs
  // --max-warnings 0.
  const people: ConversationEnvelope['people'] = Object.fromEntries(
    turns
      .map((t) => {
        const turn = t as { employee_id?: string | null; employee_name?: string | null };
        return [turn.employee_id, turn.employee_name] as const;
      })
      .filter((pair): pair is readonly [string, string] => Boolean(pair[0] && pair[1]))
      .map(([id, name]) => [id, { name }]),
  );

  return {
    people,
    thread: detail
      ? {
          id: (detail as { id?: string }).id,
          tenant_id: (detail as { tenant_id?: string }).tenant_id,
          title: (detail as { title?: string }).title,
          source: (detail as { source?: string }).source,
        }
      : undefined,
    messages: turns.map((t) => ({
      // turn ids are not uuid7, so flatten() falls back to the timestamp — which
      // conversation_turns always has
      id: (t as { id?: string }).id,
      role: t.role === 'user' ? 'employee' : 'agent',
      // conversations-viewer leaves employee_id null but populates
      // employee_name, so carry the name directly — the viewer prefers it
      // over a people[] lookup
      employee_name: (t as { employee_name?: string | null }).employee_name
        ?? (t.role === 'user'
          ? (detail as { initiated_by_employee_name?: string | null } | null | undefined)
            ?.initiated_by_employee_name
          : null),
      content: t.message_text,
      timestamp: t.timestamp,
      employee_id: (t as { employee_id?: string }).employee_id,
      is_internal: false,
      message_metadata: (t as { extra_data?: Record<string, unknown> }).extra_data ?? null,
    })),
    runs: [],
  };
}

/** Raw S3 export -> the internal track.
 *
 * Each agent_response is one run. Its pydantic_ai.all_messages is the Logfire
 * shape ({role, parts:[{type}]}), which flatten() normalizes itself — do not
 * pre-convert here or it will be normalized twice. */
export function fromRaw(raw?: RawConversation | null): ConversationEnvelope {
  const runs: Record<string, unknown>[] = [];
  for (const turn of raw?.turns ?? []) {
    for (const resp of turn.agent_responses) {
      const attrs = resp.attributes;
      runs.push({
        id: resp.span_id,
        created_at: resp.end_timestamp || resp.timestamp,
        messages: attrs['pydantic_ai.all_messages'],
        state: null,
        metadata: {
          agent_name: attrs.agent_name,
          model_name: attrs.model_name,
          input_tokens: attrs['gen_ai.usage.input_tokens'],
          output_tokens: attrs['gen_ai.usage.output_tokens'],
        },
      });
    }
  }
  return {
    thread: raw
      ? { id: raw.conversation_id, tenant_id: raw.tenant_id, created_at: raw.started_at }
      : undefined,
    runs,
  };
}

/** A pre-built timeline (kb-insights merges chatbot turns with thread
 *  messages and labels the speakers itself) -> the visible track.
 *
 *  The caller's `senderLabel` is passed as the name: the viewer prefers an
 *  explicit name over a people[] lookup, so Reporter/Assignee labels survive.
 *  A synthetic system message marks the chatbot -> service-desk boundary,
 *  which the viewer renders as a centred pill. */
export function fromTimeline(
  entries: {
    id: string;
    kind?: string;
    role: string;
    content: string;
    timestamp: string;
    endTimestamp?: string | null;
    senderLabel?: string | null;
    isInternal: boolean;
  }[],
): ConversationEnvelope {
  const messages: Record<string, unknown>[] = [];
  let lastKind: string | undefined;
  for (const e of entries) {
    if (lastKind === 'chatbot' && e.kind && e.kind !== 'chatbot') {
      messages.push({
        id: `divider-${e.id}`,
        role: 'system',
        system_actor: 'Service Desk Thread',
        content: 'Service Desk Thread',
        timestamp: e.timestamp,
        is_internal: false,
      });
    }
    lastKind = e.kind;
    messages.push({
      id: e.id,
      role: e.role === 'agent' ? 'agent' : 'employee',
      content: e.content,
      timestamp: e.role === 'agent' && e.endTimestamp ? e.endTimestamp : e.timestamp,
      employee_name: e.senderLabel ?? null,
      is_internal: e.isInternal,
    });
  }
  return { messages, runs: [] };
}

/** Merge the two tracks into one envelope. Either may be missing. */
export function toEnvelope(
  detail?: ConversationDetail | null,
  raw?: RawConversation | null,
  opts?: { threadId?: string | null },
): ConversationEnvelope {
  const d = fromDetail(detail);
  const r = fromRaw(raw);
  const thread = { ...(r.thread ?? {}), ...(d.thread ?? {}) };
  const people = d.people ?? {};
  const threadId = opts?.threadId ?? (thread.id as string | undefined);
  return {
    thread,
    people,
    messages: d.messages ?? [],
    runs: r.runs,
    links: threadId ? { logfire: logfireUrl(String(threadId)) } : undefined,
  };
}
